<template>
  <div class="mt-6 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
    <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
      {{ isEditing ? 'Editar Dirección' : 'Nueva Dirección' }}
    </h3>
    
    <form
      class="space-y-4"
      @submit.prevent="handleSubmit"
    >
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="md:col-span-2">
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Dirección *
          </label>
          <input
            v-model="form.address"
            type="text"
            required
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="Calle Principal #123"
          >
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Apartamento/Casa
          </label>
          <input
            v-model="form.apartment"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="Apto 2B"
          >
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Colonia/Barrio
          </label>
          <input
            v-model="form.neighborhood"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="Centro"
          >
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Ciudad
          </label>
          <input
            v-model="form.city"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="Ciudad de México"
          >
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Código Postal
          </label>
          <input
            v-model="form.postal_code"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            placeholder="06000"
          >
        </div>
      </div>

      <div>
        <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Punto de Referencia
        </label>
        <textarea
          v-model="form.reference_point"
          rows="2"
          class="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          placeholder="Frente al parque, esquina con tienda OXXO"
        />
      </div>

      <div class="flex items-center">
        <input
          id="is_default"
          v-model="form.is_default"
          type="checkbox"
          class="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 dark:border-gray-700 rounded"
        >
        <label
          for="is_default"
          class="ml-2 block text-sm text-gray-700 dark:text-gray-300"
        >
          Establecer como dirección predeterminada
        </label>
      </div>

      <div class="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          class="px-4 py-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
          @click="$emit('cancel')"
        >
          Cancelar
        </button>
        <button
          type="submit"
          :disabled="isSubmitting"
          class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          <LoadingSpinner 
            v-if="isSubmitting" 
            size="sm" 
            color="slate" 
            :show-text="false"
            container-class="-ml-1 mr-3"
          />
          {{ isSubmitting ? 'Guardando...' : 'Guardar' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { CustomerAddress } from '@/types/customer';
import LoadingSpinner from '@/components/base/LoadingSpinner.vue';

interface Props {
  isEditing?: boolean;
  isSubmitting?: boolean;
  formData: {
    address: string;
    apartment: string;
    neighborhood: string;
    city: string;
    postal_code: string;
    reference_point: string;
    is_default: boolean;
  };
}

interface Emits {
  (e: 'submit', data: any): void;
  (e: 'cancel'): void;
  (e: 'update:address', value: string): void;
  (e: 'update:apartment', value: string): void;
  (e: 'update:neighborhood', value: string): void;
  (e: 'update:city', value: string): void;
  (e: 'update:postal_code', value: string): void;
  (e: 'update:reference_point', value: string): void;
  (e: 'update:is_default', value: boolean): void;
}

const props = withDefaults(defineProps<Props>(), {
  isEditing: false,
  isSubmitting: false,
});

const emit = defineEmits<Emits>();

const form = computed({
  get: () => props.formData,
  set: (value) => {
    if (value.address !== undefined) emit('update:address', value.address);
    if (value.apartment !== undefined) emit('update:apartment', value.apartment);
    if (value.neighborhood !== undefined) emit('update:neighborhood', value.neighborhood);
    if (value.city !== undefined) emit('update:city', value.city);
    if (value.postal_code !== undefined) emit('update:postal_code', value.postal_code);
    if (value.reference_point !== undefined) emit('update:reference_point', value.reference_point);
    if (value.is_default !== undefined) emit('update:is_default', value.is_default);
  }
});

const handleSubmit = () => {
  emit('submit', props.formData);
};
</script>
